<template>
    <div class="w-full h-full">
        <FormModal v-if="showFormModal" @cancel="showFormModal = false"
            @confirm="updateAnalytics(); showFormModal = false" title="Edit timeframe"
            subtitle="Edit analytics timeframe" :valid="formStart != '' && formEnd != ''" submitButtonText="Apply">

            <CustomInput v-model="formStart" type="date" label="Start date" />
            <CustomInput v-model="formEnd" type="date" label="End date" />
            <CustomSelect v-model="interval" :preSelect="'week'"
                :options="[{ id: 'day', text: 'Day' }, { id: 'week', text: 'Week' }, { id: 'month', text: 'Month' }, { id: 'year', text: 'Year' },]" />

        </FormModal>
        <Header title="Retailer" icon="Store" />
        <Breadcrumbs v-if="finishedloading" :text1="retailer.retailerName" :link1='`${retailerId}`'>
        </Breadcrumbs>

        <LoadingPlaceholder :loading="!finishedloading" :subheader="true" :stats="true" :charts="true" :table="true" />

        <SubHeader v-if="finishedloading" :logo="retailer.logo" :title="retailer.retailerName"
            :subText="retailer.personalisationCategories == null ? '' : retailer.personalisationCategories[0]"
            icon="Store">
            <template v-slot:buttons>
                <a v-if="formattedRetailerWebsite != null" class="" :href="formattedRetailerWebsite" target=”_blank”>
                    <CustomButton id="website" small text="Website" icon="ExternalLink" type="navigate" />
                </a>
            </template>
        </SubHeader>

        <WhiteBox v-if="finishedloading" title="Analytics"
            :subText="`${niceDateTime(startDate)} to ${niceDateTime(endDate)}`">
            <template v-slot:buttons>
                <CustomButton id="createContract" small text="Edit timeframe" type="action" icon="CalendarDays"
                    @click="showFormModal = true" />

            </template>
            <template v-slot:content>
                <div v-if="analytics" class="grid grid-cols-2 lg:grid-cols-4 gap-1">
                    <StatBox class="col-span-1 w-full" name="Spend" currency="£" icon="CreditCard">
                        <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                            <NumberMaker :number="Math.abs(analytics.transactionVolume)" :commas="true"
                                :percentage="false" :decimals="2" currency="£" />
                        </p>
                    </StatBox>
                    <StatBox class="col-span-1 w-full" name="Transactions" icon="CreditCard">
                        <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                            <NumberMaker :number="analytics.transactionCount" :commas="true" :decimals="0"
                                currency="" />
                        </p>
                    </StatBox>
                    <StatBox class="col-span-1 w-full" name="Cashback" icon="HandCoins">
                        <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                            <NumberMaker :number="analytics.cashbackVolume" :commas="true" :percentage="false"
                                :decimals="2" currency="£" />
                        </p>
                    </StatBox>
                    <StatBox class="col-span-1 w-full" name="Cashback events" currency="£" icon="Banknote">
                        <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                            <NumberMaker :number="analytics.cashbackCount" :commas="true" :percentage="false"
                                :decimals="0" currency="" />
                        </p>
                    </StatBox>
                    <StatBox class="col-span-1 lg:col-span-2 w-full" percentage name="Share of total spend:"
                        :number="((analytics.transactionVolume / allTransactionsAnalysis.totalTransactionVolume) * 100).toFixed(4)"
                        icon="Banknote">
                    </StatBox>
                    <StatBox class="col-span-1 lg:col-span-2 w-full" percentage
                        :name="'Share of ' + (retailer.personalisationCategories ? retailer.personalisationCategories[0]?.toLowerCase() : 'Unknown') + ' category'"
                        :number="((analytics.transactionVolume / categoryTransactionAnalysis.totalTransactionVolume) * 100).toFixed(4)"
                        icon="BadgePercent">
                    </StatBox>
                </div>
                <div v-if="analytics" class=" hidden lg:grid grid-cols-2 gap-1 mt-2">
                    <div class="flex items-center">
                        <Bar v-if="transactionChartData != undefined" :data="transactionChartData"
                            :options="chartOptions" />
                    </div>
                    <div class="flex items-center">
                        <Bar v-if="transactionVolumeChart != undefined" :data="transactionVolumeChart"
                            :options="chart2Options" />
                    </div>

                </div>

            </template>
        </WhiteBox>


        <WhiteBox v-if="finishedloading" title="Offers available"
            subText="View the available offers across our network">
            <template v-slot:buttons>
                <CustomButton small id="filterButton" text="Add your own offer" icon="Plus" type="action"
                    @click="$router.push('/add-offer')" />
            </template>
            <template v-slot:content>
                <EasyDataTable :headers="headersTable1" :items="finalOffersAvalaible" :loading="!finishedloading"
                    :hide-footer="false" :no-hover="true">
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                        <div class="flex justify-center py-4">
                            <div
                                class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                <p class="text-sm mr-2">No results</p>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                </svg>
                            </div>
                        </div>
                    </template>

                    <template #item-retailer="retailers">
                        <LogoAndText :logo="retailers.retailer.logo" :text="retailers.retailer.retailerName" />
                    </template>
                    <template #item-supplier="retailers">

                        <Supplier :supplier="retailers.supplier" />
                    </template>
                    <template #item-offertype="retailers">
                        <OfferTypePill :type="retailers.offerType" />
                    </template>
                    <template #item-cashbackAmount="retailers">
                        <CashbackPill :text="retailers.cashbackRules[0].conditions[0].value.maxAvailableValue"
                            :type="retailers.cashbackRules[0].conditions[0].value.type"
                            :currency="retailers.currency" />
                    </template>
                    <template #header-action="header">
                        <div class="w-full text-right">
                            {{ header.text }}
                        </div>
                    </template>
                    <template #item-action="retailers">
                        <div class="flex justify-end items-center py-1 gap-1">
                            <CustomButton small text="View Offer" type="navigate"
                                @click="$router.push(`/retailer/${retailers.retailer.id}/offer/${retailers.id}`)" />
                        </div>
                    </template>
                </EasyDataTable>
            </template>
        </WhiteBox>
    </div>
</template>

<script setup>
</script>

<script>
import Header from "@/components/Header.vue";
import SubHeader from "@/components/SubHeader.vue";
import WhiteBox from "@/components/WhiteBox.vue";
import Breadcrumbs from "@/components/navbars/Breadcrumbs.vue";
import CustomButton from "@/components/inputs/CustomButton.vue";
import LogoAndText from "@/components/LogoAndText.vue";
import CashbackPill from "@/components/CashbackPill.vue";
import OfferTypePill from "@/components/OfferTypePill.vue";
import Supplier from "@/components/Supplier.vue";
import StatBox from "@/components/StatBox.vue";
import FormModal from "@/components/modals/FormModal.vue";
import CustomInput from '@/components/inputs/CustomInput.vue';
import CustomSelect from "@/components/inputs/CustomSelect.vue";
import NumberMaker from "@/components/NumberMaker.vue";
import LoadingPlaceholder from "@/components/LoadingPlaceholder.vue";

import { DateTime } from 'luxon';

import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { Colors } from 'chart.js';

ChartJS.register(Colors);

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
    name: "manage-publsihers",
    components: {
        Header,
        SubHeader,
        WhiteBox,
        Breadcrumbs,
        CustomButton,
        LogoAndText,
        CashbackPill,
        OfferTypePill,
        Supplier,
        StatBox,
        FormModal,
        CustomInput,
        Bar,
        CustomSelect,
        NumberMaker,
        LoadingPlaceholder
    },
    props: {
    },

    data() {
        return {
            retailerId: this.$route.params.retailerId,
            retailer: undefined,
            showErrorModal: false,
            showSuccessModal: false,
            showFormModal: false,
            loading: true,
            loadingActivatedOffers: true,
            loadingOffers: true,
            loadingRetailer: true,

            headersTable1: [
                { text: "Retailer", value: "retailer" },
                { text: "Supplier", value: "supplier" },
                { text: "Offer type", value: "offertype" },
                { text: "Total cashback amount", value: "cashbackAmount" },
                { text: "Action", value: "action" },
            ],

            activatedOffersTableHeader: [
                { text: "Retailer", value: "retailer" },
                { text: "Supplier", value: "supplier" },
                { text: "Offer type", value: "offertype" },
                { text: "Cashback amount", value: "cashbackAmount" },
                { text: "Availability status", value: "active" },
                { text: "Approval status", value: "offerApproval" },
                { text: "Action", value: "action" },
            ],
            offerIds: undefined,

            offers: [

            ],
            activatedOffers: [],

            interval: 'week',

            chartOptions: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top'
                    },
                    title: {
                        display: true,
                        text: 'Transaction count'
                    }
                }
            },

            chart2Options: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top'
                    },
                    title: {
                        display: true,
                        text: 'Transaction volume'
                    }
                }
            },

            channelTable: [
                { text: "Channel name", value: "channelName" },
                { text: "Address", value: "address" },
                { text: "City", value: "city" },
                { text: "Postcode", value: "postcode" },
                { text: "Channel Type", value: "channelType" },
                { text: "Mids", value: "mids" }

            ],
            startDate: '',
            endDate: '',
            analytics: undefined,
            allTransactionsAnalysis: undefined,
            transactionChartData: undefined,
            transactionVolumeChart: undefined,

            channels: [],
            // showFormModal:false,
            formStart: '',
            formEnd: ''
        }
    },
    created() {


        if (this.$route.query.start && this.$route.query.end) {
            this.formStart = this.$route.query.start
            this.formEnd = this.$route.query.end
            this.startDate = DateTime.fromFormat(this.formStart,'dd-mm-yyyy').startOf('day');
            this.endDate = DateTime.fromFormat(this.formEnd,'dd-mm-yyyy').endOf('day');


        } else {
            const now = DateTime.local();
            const startOfMonth = now.startOf('month');
            const endOfMonth = now.endOf('month');

            this.startDate = startOfMonth.minus({ month: 2 });
            this.endDate = endOfMonth;
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

        }

        this.getRetailer()
        this.getActivatedOffers(0, 50)

    },
    methods: {

        fillMissingDates(data, interval = { weeks: 1 }) {
            const { DateTime } = require("luxon"); // Ensure DateTime is imported

            // Convert date strings to DateTime objects for sorting and iteration
            const sortedData = data
                .map(entry => ({
                    ...entry,
                    id: DateTime.fromFormat(entry.id, "EEE MMM dd HH:mm:ss z yyyy").startOf(this.interval) // Parse to DateTime
                }))
                .sort((a, b) => a.id - b.id);

            // Define start and end dates as DateTime objects
            const startDate = this.startDate.startOf(this.interval)
            const endDate = this.endDate.endOf(this.interval);

            const filledData = [];
            let currentDate = startDate;

            // Fill in missing weekly dates
            while (currentDate <= endDate) {
                // Find if an entry exists for the currentDate week
                const existingEntry = sortedData.find(entry => entry.id.equals(currentDate));
                if (existingEntry) {
                    filledData.push(existingEntry);
                } else {
                    filledData.push({
                        id: currentDate,
                        transactionCount: 0,
                        transactionVolume: 0
                    });
                }

                // Increment by the interval (e.g., weekly)
                currentDate = currentDate.plus(interval);
            }

            // Convert DateTime objects back to strings for final output
            return filledData.map(entry => ({
                ...entry,
                id: entry.id.toFormat("EEE MMM dd HH:mm:ss z yyyy")
            }));
        },



        formatData(data) {
            var x = {}
            x[this.interval] = 1

            var val = this.fillMissingDates(data, x)

            this.transactionChartData = {
                labels: val.map((data) => DateTime.fromFormat(data.id, "EEE MMM dd HH:mm:ss z yyyy").toLocaleString(DateTime.DATE_SHORT)),
                datasets: [
                    {
                        label: "Transaction Count",
                        backgroundColor: "rgba(55, 162, 235, 1)",
                        borderColor: "rgba(55, 162, 235, 1))",
                        borderRadius: 3,
                        data: val.map((data) => data.transactionCount),
                    }
                ]
            }
            this.transactionVolumeChart = {
                labels: val.map((data) => DateTime.fromFormat(data.id, "EEE MMM dd HH:mm:ss z yyyy").toLocaleString(DateTime.DATE_SHORT)),
                datasets: [
                    {
                        label: "Transaction Volume",
                        backgroundColor: "rgba(55, 162, 235, 1)",
                        borderColor: "rgba(55, 162, 235, 1))",
                        borderRadius: 3,
                        data: val.map((data) => Math.abs(data.transactionVolume)),
                    },
                ]
            }
        },

        updateAnalytics() {
            this.startDate = DateTime.fromISO(this.formStart).startOf('day');
            this.endDate = DateTime.fromISO(this.formEnd).endOf('day');
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

            this.getRetailer()

        },

        niceDateTime(dateObject) {
            return DateTime.fromISO(dateObject).toLocaleString(DateTime.DATE_SHORT)
        },

        async getRetailer() {
            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')

                this.loadingRetailer = true

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/catalogue/retailer/${this.retailerId}?startDate=${this.formStart}&endDate=${this.formEnd}&interval=${this.interval}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.offerIds = object.offers
                    this.retailer = object
                    this.analytics = object.retailerAnalytics
                    this.channels = object.channels
                    this.allTransactionsAnalysis = object.allTransactionsAnalysis
                    this.categoryTransactionAnalysis = object.relevantCategoriesTransactionsAnalysis
                    this.graphData = this.formatData(object.graphData)
                    this.getOffers(this.offerIds)
                    this.loadingRetailer = false

                }

            } catch (error) {
                return;
            }

        },

        async getOffers(offerIds) {
            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')
                this.loadingOffers = true

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/catalogue/offer-by-ids?offerId=${offerIds}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.offers = object
                    this.loadingOffers = false
                }

            } catch (error) {
                return;
            }

        },



        async getActivatedOffers(page, size) {
            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')
                this.loadingActivatedOffers = true
                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/activated-offer/retailer/${this.retailerId}?page=${page}&size=${size}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.activatedOffers = object.content

                    this.loadingActivatedOffers = false
                }

            } catch (error) {
                return;
            }

        },

    },
    computed: {
        finishedloading() {
            return this.loadingActivatedOffers == false && this.loadingOffers == false;
        },
        finalOffersAvalaible() {

            var x = this.offers

            return x
        },
        formattedRetailerWebsite() {

            if (this.retailer.retailerWebsite == null) {
                return null
            }

            // Check if retailerWebsite starts with "https://" or "http://"
            if (this.retailer.retailerWebsite.startsWith('https://') || this.retailer.retailerWebsite.startsWith('http://')) {
                return this.retailer.retailerWebsite;
            } else {
                // If it doesn't, add "https://" prefix
                return `https://${this.retailer.retailerWebsite}`;
            }
        }

    },
    mounted() {
        document.title = "Dashboard | Retailer";
    },
    watch: {
    }
}
</script>